import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { patientsApi } from '@/api/patients';
import { publisherResultImportsApi } from '@/api/publisher-result-imports';
import { ClipboardTick, ClipboardText, Edit, DocumentText, User } from '@/components/icons';
import PatientAssignModal from '@/components/patients/PatientAssignModal';
import PatientDialog from '@/components/patients/PatientDialog';
import { PublisherResultImportModal } from '@/components/publisher-results/PublisherResultImportModal';
import { PublisherResultImportsPanel } from '@/components/publisher-results/PublisherResultImportsPanel';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { DetailTabsSection, EmptyState, PageHeader } from '@/components/system';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';
import { useModalContext } from '@/components/ui/modal-provider';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { usePatientAnamnesis } from '@/hooks/use-patient-anamnesis';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { isPublisherResultImportActive } from '@/lib/publisher-result-imports';
import WrapperLayout from '@/pages/components/WrapperLayout';
import { PatientsPage, ReportPage, SurveyResponsePage } from '@/pages/constants';
import { useAnamnesisActions } from '@/pages/patients/anamnesis/use-anamnesis-actions';
import PatientAnamnesisTab from '@/pages/patients/components/PatientAnamnesisTab';
import PatientHero from '@/pages/patients/components/PatientHero';
import PatientReportsTable from '@/pages/patients/components/PatientReportsTable';
import PatientStatusStrip from '@/pages/patients/components/PatientStatusStrip';
import PatientSurveysTable from '@/pages/patients/components/PatientSurveysTable';
import PatientTestsTable from '@/pages/patients/components/PatientTestsTable';
import TestAnswersModal from '@/pages/patients/components/TestAnswersModal';
import { createPageUrl, getReportLink } from '@/utils';
import type { PatientStatusTab } from '@/pages/patients/components/patient-status';

const IMPORTS_PAGE_SIZE = 10;

export default function PatientDetails() {
    const { patientId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { openModal, openWizardModal, closeModal } = useModalContext();
    const queryClient = useQueryClient();
    const [isPatientDialogOpen, setIsPatientDialogOpen] = useState(false);
    const [importsPage, setImportsPage] = useState(1);
    const [activeTab, setActiveTab] = useState<PatientStatusTab>('reports');
    const backTo = (location.state as { backTo?: string } | null)?.backTo;
    const sendSurveyAccess = useFeatureEntitlement('surveys.send');
    const sendTestAccess = useFeatureEntitlement('tests.send');
    const publisherResultImportsFeature = useFeatureFlag('publisherResultImports.reviewFlow');
    const canUsePublisherResultImports = publisherResultImportsFeature.enabled;

    const { data, isLoading } = useQuery({
        queryKey: ['patients', patientId, 'overview'],
        queryFn: () => patientsApi.getOverview(patientId || ''),
        enabled: !!patientId,
    });

    const { data: anamnesis, isLoading: isLoadingAnamnesis } = usePatientAnamnesis(patientId);
    const anamnesisActions = useAnamnesisActions(patientId, data?.patient);

    const { data: publisherResultImportsResponse, isLoading: isLoadingImports } = useQuery({
        queryKey: ['publisher-result-imports', 'patient', patientId, importsPage],
        queryFn: () =>
            publisherResultImportsApi.listPatientImports(patientId || '', {
                page: importsPage,
                pageSize: IMPORTS_PAGE_SIZE,
            }),
        enabled: !!patientId && canUsePublisherResultImports,
        refetchInterval: (query) =>
            query.state.data?.data.some(isPublisherResultImportActive) ? 2_500 : false,
    });

    const regeneratePhotoMutation = useMutation({
        mutationFn: () => {
            if (!patientId) throw new Error('Patient ID is missing');
            return patientsApi.regeneratePhoto(patientId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['patients'] });
            queryClient.invalidateQueries({
                queryKey: ['patients', patientId, 'overview'],
            });
            toast.success('Foto do paciente regenerada com sucesso');
        },
        onError: (error) => {
            console.error(error);
            toast.error('Não foi possível regenerar a foto do paciente.');
        },
    });

    useEffect(() => {
        setImportsPage(1);
        setActiveTab('reports');
    }, [patientId]);

    const patient = data?.patient;
    const surveys = data?.surveys || [];
    const tests = data?.tests || [];
    const reports = data?.reports || [];
    const ongoingReport = reports.find((report) => report.status === 'in_progress');
    const publisherResultImports = publisherResultImportsResponse?.data ?? [];
    const publisherResultImportsPagination = publisherResultImportsResponse?.pagination;
    const publisherResultImportsCount =
        publisherResultImportsPagination?.totalCount ?? publisherResultImports.length;
    const activeImports = publisherResultImports.filter(isPublisherResultImportActive).length;

    const handleSendSurvey = () => {
        if (!patient) return;
        if (sendSurveyAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(sendSurveyAccess.decision));
            return;
        }

        openWizardModal({
            content: <PatientAssignModal patient={patient} mode="surveys" />,
            size: 'md',
            className: 'max-h-[95vh]',
        });
    };

    const handleCreateReport = () => {
        if (!patient) return;

        if (ongoingReport) {
            navigate(getReportLink(ongoingReport.id));
            return;
        }

        navigate(createPageUrl(ReportPage), {
            state: {
                preSelectedPatient: {
                    id: patient.id,
                    full_name: patient.full_name,
                    date_of_birth: patient.date_of_birth,
                    age: patient.age,
                    gender: patient.gender,
                    avatar_url: patient.avatar_url,
                },
            },
        });
    };

    const handleSendTest = () => {
        if (!patient) return;
        if (sendTestAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(sendTestAccess.decision));
            return;
        }

        openWizardModal({
            content: <PatientAssignModal patient={patient} mode="tests" />,
            size: 'lg',
            className: 'max-h-[95vh]',
        });
    };

    const handleViewTestAnswers = (payload: { assignmentId: string; testName: string }) => {
        if (!patient) return;
        openModal({
            title: `Resultados do instrumento`,
            description: payload.testName,
            size: 'lg',
            content: (
                <TestAnswersModal patientId={patient.id} assignmentId={payload.assignmentId} />
            ),
        });
    };

    const handleRegeneratePhoto = () => {
        if (!patientId || regeneratePhotoMutation.isPending) return;
        regeneratePhotoMutation.mutate();
    };

    const invalidatePublisherResultImports = () => {
        queryClient.invalidateQueries({
            queryKey: ['publisher-result-imports', 'patient', patientId],
        });
        queryClient.invalidateQueries({
            queryKey: ['patients', patientId, 'overview'],
        });
        queryClient.invalidateQueries({ queryKey: ['reports'] });
    };

    const openPublisherResultImport = (batchId?: string | null, importId?: string | null) => {
        if (!patient || !canUsePublisherResultImports) return;

        openWizardModal({
            title: null,
            size: 'xl',
            className: 'max-h-[95vh]',
            showCloseButton: false,
            content: (
                <PublisherResultImportModal
                    patientId={patient.id}
                    mode="patient"
                    initialBatchId={batchId ?? null}
                    initialImportId={importId ?? null}
                    onCompleted={invalidatePublisherResultImports}
                    onClose={closeModal}
                />
            ),
        });
    };

    return (
        <WrapperLayout
            title="Paciente"
            subtitle="Resumo e atividades do paciente"
            showHeader={false}
        >
            {isLoading ? (
                <Loading />
            ) : !patient ? (
                <EmptyState
                    icon={User}
                    title="Paciente não encontrado"
                    description="O registro pode ter sido removido ou não está mais acessível."
                    action={
                        <Button onClick={() => navigate(backTo || createPageUrl(PatientsPage))}>
                            Voltar
                        </Button>
                    }
                />
            ) : (
                <>
                    <PageHeader
                        variant="workbench"
                        backTo={backTo || createPageUrl(PatientsPage)}
                        breadcrumb={[
                            { label: 'Pacientes', to: createPageUrl(PatientsPage) },
                            { label: patient.full_name },
                        ]}
                        actions={
                            <>
                                <Button onClick={handleSendSurvey} variant="surface">
                                    <ClipboardText className="size-4" />
                                    Enviar formulário
                                    {sendSurveyAccess.isUnavailableInPlan &&
                                    sendSurveyAccess.availableFromPlan ? (
                                        <FeatureUnavailableChip
                                            availablePlan={sendSurveyAccess.availableFromPlan}
                                        />
                                    ) : null}
                                </Button>
                                <Button onClick={handleSendTest} variant="surface">
                                    <ClipboardTick className="size-4" />
                                    Enviar instrumento
                                    {sendTestAccess.isUnavailableInPlan &&
                                    sendTestAccess.availableFromPlan ? (
                                        <FeatureUnavailableChip
                                            availablePlan={sendTestAccess.availableFromPlan}
                                        />
                                    ) : null}
                                </Button>
                                <Button onClick={handleCreateReport}>
                                    <DocumentText className="size-4" />
                                    {ongoingReport ? 'Continuar laudo' : 'Novo laudo'}
                                </Button>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => setIsPatientDialogOpen(true)}
                                    aria-label="Editar paciente"
                                    title="Editar paciente"
                                >
                                    <Edit className="size-4" />
                                </Button>
                            </>
                        }
                    />

                    <PatientHero
                        patient={patient}
                        isRegeneratingPhoto={regeneratePhotoMutation.isPending}
                        onRegeneratePhoto={handleRegeneratePhoto}
                        anamnesis={anamnesis}
                    />

                    <PatientStatusStrip
                        surveys={surveys}
                        tests={tests}
                        reports={reports}
                        anamnesis={anamnesis}
                        activeImports={canUsePublisherResultImports ? activeImports : 0}
                        onSelectTab={setActiveTab}
                    />

                    <DetailTabsSection
                        value={activeTab}
                        onValueChange={(value) => setActiveTab(value as PatientStatusTab)}
                        tabs={[
                            {
                                value: 'reports',
                                label: 'Laudos',
                                count: reports.length,
                                content: (
                                    <PatientReportsTable
                                        reports={reports}
                                        onOpenReport={(reportId) =>
                                            navigate(getReportLink(reportId))
                                        }
                                        emptyAction={
                                            <Button onClick={handleCreateReport} variant="default">
                                                <DocumentText className="size-4" />
                                                Novo Laudo
                                            </Button>
                                        }
                                    />
                                ),
                            },
                            {
                                value: 'anamnesis',
                                label: 'Anamnese',
                                count: anamnesis?.source ? 1 : undefined,
                                content: (
                                    <PatientAnamnesisTab
                                        anamnesis={anamnesis}
                                        isLoading={isLoadingAnamnesis}
                                        onCreate={anamnesisActions.create}
                                        onOpen={() => anamnesisActions.open()}
                                        onDelete={() => anamnesisActions.remove()}
                                    />
                                ),
                            },
                            {
                                value: 'surveys',
                                label: 'Formulários',
                                count: surveys.length,
                                content: (
                                    <PatientSurveysTable
                                        patientId={patient.id}
                                        surveys={surveys}
                                        onViewAnswers={(reportId) =>
                                            navigate(
                                                `/${generatePath(SurveyResponsePage, {
                                                    reportId,
                                                })}`,
                                            )
                                        }
                                        emptyAction={
                                            <Button onClick={handleSendSurvey}>
                                                <ClipboardText className="size-4" />
                                                Enviar formulário
                                            </Button>
                                        }
                                    />
                                ),
                            },
                            {
                                value: 'tests',
                                label: 'Instrumentos',
                                count: tests.length,
                                content: (
                                    <PatientTestsTable
                                        patientId={patient.id}
                                        tests={tests}
                                        onViewAnswers={handleViewTestAnswers}
                                        emptyAction={
                                            <Button onClick={handleSendTest}>
                                                <ClipboardTick className="size-4" />
                                                Enviar instrumento
                                            </Button>
                                        }
                                    />
                                ),
                            },
                            ...(canUsePublisherResultImports
                                ? [
                                      {
                                          value: 'import-results',
                                          label: 'Importar resultado',
                                          count: publisherResultImportsCount,
                                          content: (
                                              <PublisherResultImportsPanel
                                                  imports={publisherResultImports}
                                                  isLoading={isLoadingImports}
                                                  pagination={publisherResultImportsPagination}
                                                  currentPage={importsPage}
                                                  onPageChange={setImportsPage}
                                                  onImport={() => openPublisherResultImport()}
                                                  onReviewImport={(batchId, importId) =>
                                                      openPublisherResultImport(batchId, importId)
                                                  }
                                              />
                                          ),
                                      },
                                  ]
                                : []),
                        ]}
                    />

                    <PatientDialog
                        open={isPatientDialogOpen}
                        onOpenChange={setIsPatientDialogOpen}
                        patient={patient}
                    />
                </>
            )}
        </WrapperLayout>
    );
}
